import React, { useState, forwardRef, useId, useCallback } from 'react';
import {
  TextField as MuiTextField,
  InputAdornment,
  useTheme,
} from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import ClearIcon from '@mui/icons-material/HighlightOff';
import InputMask from 'react-input-mask';
import { TextFieldProps, SizeKey } from './TextField.props';
import {
  formatValue,
  getPatternForFormat,
  getPlaceholderForFormat,
} from './inputFormatUtils';

// Size configurations for custom sizes
const SIZE_CONFIG: Record<SizeKey, { height: string; padding: string }> = {
  m: { height: '56px', padding: '16px' },
};

export const TextField = forwardRef<HTMLDivElement, TextFieldProps>(
  (
    {
      prefixIcon,
      suffixIcon,
      onClear,
      error,
      value: controlledValue,
      defaultValue,
      showClearIcon = false,
      disabled,
      mask,
      maskChar = '_',
      pattern,
      inputProps,
      prefixIconSx,
      suffixIconSx,
      clearIconSx,
      size = 'm',
      format = 'none',
      borderColor,
      labelColor,
      placeholderColor,
      textColor,
      placeholder,
      onChange,
      sx,
      variant = 'outlined',
      fullWidth = true,
      label,
      helperText,
      required,
      InputProps,
      InputLabelProps,
      FormHelperTextProps,
      ...props
    },
    ref
  ) => {
    const theme = useTheme();
    const inputId = useId();

    // Internal state for uncontrolled component
    const [internalValue, setInternalValue] = useState<string>(
      (defaultValue as string) || ''
    );
    const [hasError, setHasError] = useState(false);
    const [touched, setTouched] = useState(false);

    // Determine if component is controlled
    const isControlled = controlledValue !== undefined;
    const currentValue = isControlled ? controlledValue : internalValue;

    // Get pattern from format if not explicitly provided
    const validationPattern =
      pattern || (format !== 'none' ? getPatternForFormat(format) : undefined);

    // Get placeholder from format if not explicitly provided
    const effectivePlaceholder =
      placeholder ||
      (format !== 'none' ? getPlaceholderForFormat(format) : undefined);

    // Validate field value
    const validateValue = useCallback(
      (value: string, inputElement?: HTMLInputElement) => {
        // Check HTML5 validity if input element is provided
        if (inputElement && !inputElement.validity.valid) {
          setHasError(true);
          return false;
        }

        // Check pattern validation
        if (validationPattern && value) {
          const isValid = new RegExp(
            validationPattern instanceof RegExp
              ? validationPattern.source
              : validationPattern
          ).test(value);
          setHasError(!isValid);
          return isValid;
        }

        // Check required validation
        if (required && (!value || value.trim() === '')) {
          setHasError(true);
          return false;
        }

        setHasError(false);
        return true;
      },
      [validationPattern, required]
    );

    // Handle blur event for validation
    const handleBlur = useCallback(
      (event: React.FocusEvent<HTMLInputElement>) => {
        setTouched(true);
        validateValue(event.target.value, event.target);

        // Call parent onBlur if provided
        if (props.onBlur) {
          props.onBlur(event);
        }
      },
      [validateValue, props]
    );

    // Handle invalid event for HTML5 validation
    const handleInvalid = useCallback(
      (event: React.InvalidEvent<HTMLInputElement>) => {
        event.preventDefault(); // Prevent default browser validation UI
        setTouched(true);
        setHasError(true);

        // Call parent onInvalid if provided
        if (props.onInvalid) {
          props.onInvalid(event);
        }
      },
      [props]
    );

    // Handle change with auto-formatting
    const handleChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        let newValue = event.target.value;

        // Apply auto-formatting if format is specified
        if (format !== 'none') {
          newValue = formatValue(newValue, format);
        }

        // Validate on change only if field has been touched
        if (touched) {
          validateValue(newValue, event.target);
        }

        // Update internal state for uncontrolled component
        if (!isControlled) {
          setInternalValue(newValue);
        }

        // Call parent onChange handler with modified event
        if (onChange) {
          const modifiedEvent = {
            ...event,
            target: { ...event.target, value: newValue },
          } as React.ChangeEvent<HTMLInputElement>;
          onChange(modifiedEvent);
        }
      },
      [format, touched, validateValue, onChange, isControlled]
    );

    const isError = error || (hasError && touched);

    // Determine helper text
    const effectiveHelperText = helperText;

    // Theme colors with fallbacks
    const defaultBorderColor = theme.palette.M3.sys.light.outline;
    const defaultLabelColor = theme.palette.M3.sys.light['on-surface-variant'];
    const defaultPlaceholderColor =
      theme.palette.M3.sys.light['on-surface-variant'];
    const defaultTextColor = theme.palette.M3.sys.light['on-surface'];

    // Get size configuration
    const currentSize = SIZE_CONFIG['m'];

    // Determine if we should show the clear icon
    const hasValue =
      currentValue !== undefined &&
      currentValue !== null &&
      (typeof currentValue !== 'string' || currentValue.length > 0);

    // Show clear icon when enabled AND has value AND has onClear handler AND not disabled
    const shouldShowClearIcon =
      showClearIcon && hasValue && !!onClear && !disabled;

    // Build start adornment (prefix icon on the left side)
    const startAdornment = prefixIcon ? (
      <InputAdornment
        position="start"
        sx={[
          {
            color: disabled
              ? theme.palette.M3.sys.light['on-surface']
              : theme.palette.M3.sys.light['on-surface-variant'],
            opacity: disabled ? 0.38 : 1,
          },
          ...(Array.isArray(prefixIconSx) ? prefixIconSx : [prefixIconSx]),
        ]}
      >
        {prefixIcon}
      </InputAdornment>
    ) : undefined;

    // Build end adornment (error icon, clear icon, or suffix icon on the right side)
    // Priority: Error icon > Clear icon > Suffix icon
    let endAdornment: React.ReactNode = undefined;

    if (isError) {
      // Show error icon if there's an error
      endAdornment = (
        <InputAdornment position="end" key="error-icon">
          <ErrorIcon color="error" />
        </InputAdornment>
      );
    } else if (shouldShowClearIcon) {
      // Show clear icon when enabled, has value, and has onClear handler
      endAdornment = (
        <InputAdornment
          position="end"
          key="clear-icon"
          sx={[
            { color: theme.palette.M3.sys.light['on-surface-variant'] },
            ...(Array.isArray(clearIconSx) ? clearIconSx : [clearIconSx]),
          ]}
        >
          <ClearIcon onClick={onClear} sx={{ cursor: 'pointer' }} />
        </InputAdornment>
      );
    } else if (suffixIcon) {
      // Show suffix icon if provided and no error/clear icon
      endAdornment = (
        <InputAdornment
          position="end"
          key="suffix-icon"
          sx={[
            {
              color: disabled
                ? theme.palette.M3.sys.light['on-surface']
                : theme.palette.M3.sys.light['on-surface-variant'],
              opacity: disabled ? 0.38 : 1,
            },
            ...(Array.isArray(suffixIconSx) ? suffixIconSx : [suffixIconSx]),
          ]}
        >
          {suffixIcon}
        </InputAdornment>
      );
    }

    // Build custom styles to match Input component exactly
    const customStyles = [
      {
        width: fullWidth ? '100%' : '660px',
        maxWidth: fullWidth ? '100%' : '660px',
        '& .MuiInputLabel-root': {
          transform: 'translate(14px, -8px) scale(0.75) !important',
          transformOrigin: 'top left',
          backgroundColor: theme.palette.M3.sys.light.surface,
          padding: '0 4px',
          zIndex: 1,
        },
        '& .MuiOutlinedInput-root': {
          height: currentSize.height,
          borderRadius: '8px',
          ...theme.typography.M3.body.large,
          color: textColor || defaultTextColor,
          '& fieldset': {
            borderColor: borderColor || defaultBorderColor,
            borderWidth: '1px',
          },
          '& fieldset legend': {
            maxWidth: '0.01px',
          },
          '&:hover fieldset': {
            borderColor: isError
              ? theme.palette.State.error
              : borderColor || defaultBorderColor,
          },
          '&.Mui-focused fieldset': {
            borderColor: isError
              ? theme.palette.State.error
              : theme.palette.M3.sys.light.primary,
            borderWidth: '2px',
          },
          '&.Mui-error fieldset': {
            borderColor: theme.palette.State.error,
          },
          '&.Mui-disabled': {
            backgroundColor: theme.palette.M3.sys.light.background,
            '& fieldset': {
              borderColor: defaultBorderColor,
            },
          },
          '& input': {
            padding: currentSize.padding,
            '&::placeholder': {
              color: placeholderColor || defaultPlaceholderColor,
              opacity: 1,
            },
          },
        },
      },
      // User-provided sx prop - spread as array to avoid conflicts
      ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
    ].filter(Boolean);

    // Input label props matching Input component
    const labelProps = {
      htmlFor: inputId,
      shrink: true,
      sx: {
        ...theme.typography.M3.body.small,
        color: labelColor || defaultLabelColor,
        transform: 'translate(14px, -8px) scale(0.75) !important',
        transformOrigin: 'top left',
        backgroundColor: theme.palette.M3.sys.light.surface,
        padding: '0 4px',
        zIndex: 1,
        '&.Mui-focused': {
          color: isError
            ? theme.palette.State.error
            : labelColor || defaultLabelColor,
          transform: 'translate(14px, -8px) scale(0.75) !important',
        },
        '&.Mui-error': {
          color: theme.palette.State.error,
        },
        '&.Mui-disabled': {
          color: defaultLabelColor,
          opacity: 0.5,
        },
      },
      ...InputLabelProps,
    };

    // Form helper text props
    const helperProps = {
      id: helperText ? `${inputId}-helper-text` : undefined,
      ...FormHelperTextProps,
    };

    // Common input props
    const commonInputProps = {
      'aria-label': props['aria-label'] || label?.toString(),
      'aria-required': required,
      'aria-invalid': isError,
      'aria-describedby': helperText ? `${inputId}-helper-text` : undefined,
      pattern:
        validationPattern instanceof RegExp
          ? validationPattern.source
          : validationPattern,
      ...inputProps,
    };

    // Common MUI input props
    const muiInputProps = {
      startAdornment,
      endAdornment,
      ...InputProps,
    };

    // If mask is provided, wrap with InputMask
    if (mask) {
      return (
        <InputMask
          mask={mask}
          value={currentValue as string}
          disabled={disabled}
          maskChar={maskChar}
          onChange={handleChange}
          onBlur={handleBlur}
        >
          {() => (
            <MuiTextField
              {...(props as any)}
              ref={ref}
              id={inputId}
              label={label}
              variant={variant}
              error={!!isError}
              value={currentValue}
              defaultValue={!isControlled ? defaultValue : undefined}
              disabled={disabled}
              placeholder={effectivePlaceholder}
              onChange={handleChange}
              onBlur={handleBlur}
              onInvalid={handleInvalid}
              fullWidth={fullWidth}
              required={required}
              helperText={effectiveHelperText}
              sx={customStyles}
              inputProps={commonInputProps}
              InputProps={muiInputProps}
              InputLabelProps={labelProps}
              FormHelperTextProps={helperProps}
            />
          )}
        </InputMask>
      );
    }

    return (
      <MuiTextField
        {...(props as any)}
        ref={ref}
        id={inputId}
        label={label}
        variant={variant}
        error={!!isError}
        value={currentValue}
        defaultValue={!isControlled ? defaultValue : undefined}
        disabled={disabled}
        placeholder={effectivePlaceholder}
        onChange={handleChange}
        onBlur={handleBlur}
        onInvalid={handleInvalid}
        fullWidth={fullWidth}
        required={required}
        helperText={effectiveHelperText}
        sx={customStyles}
        inputProps={commonInputProps}
        InputProps={muiInputProps}
        InputLabelProps={labelProps}
        FormHelperTextProps={helperProps}
      />
    );
  }
);

TextField.displayName = 'TextField';

export default TextField;
